// Public portfolio page (EPD-10101 / EPD-10102), rendered by
// `/portfolio/$profileSlug` on the marketing-domain portfolio host (EPD-10104,
// EPD-12500) and the app origin.
//
// Draft/live gating is enforced server-side: getPublicPortfolio returns
// NOT_FOUND for an unpublished (or non-existent) portfolio, so a Draft leaks no
// content here.

import { PortfolioPublicView } from "./portfolio-public-view";
import { ThemeProvider } from "@/components/theme-provider";
import { parsePortfolioThemeJson } from "@/lib/portfolio/theme";
import {
  revealPortfolioContactEmail,
  usePublicHighlightStatsQuery,
  usePublicPortfolioQuery,
  usePublicSocialStatsQuery,
  useTrackPortfolioView,
} from "@/lib/queries/public-profile";
import { Button, Skeleton } from "@repo/ui";
import { TRPCClientError } from "@trpc/client";
import { useCallback, useEffect, useRef, type ReactNode } from "react";

function PublicPortfolioSkeleton() {
  return (
    <div className="mx-auto max-w-2xl space-y-6 px-6 py-12">
      <Skeleton className="h-24 w-24 rounded-full" />
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-40 w-full rounded-xl" />
    </div>
  );
}

export function PublicPortfolioPending() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <PublicPortfolioSkeleton />
    </ThemeProvider>
  );
}

export function PublicPortfolioPage({ profileSlug }: { profileSlug: string }) {
  const { data, isPending, error, refetch, isRefetching } =
    usePublicPortfolioQuery(profileSlug);
  // Only NOT_FOUND means "missing or unpublished"; anything else (network,
  // 5xx) is transient and must not tell a live portfolio's visitor it's gone.
  const isNotFound =
    error instanceof TRPCClientError && error.data?.code === "NOT_FOUND";

  // Stats + demographics stream in once the portfolio is confirmed live; a
  // failure here just omits those blocks rather than breaking the page. While
  // they load, the block keeps a placeholder so the page doesn't jump when
  // they land (EPD-12469). A failure ends `isPending` and drops it, but only
  // after the query's retries, so that rare case still shifts the page once.
  // Accepted: the retries rescue far more loads than they delay.
  const { data: socialStats, isPending: socialStatsPending } =
    usePublicSocialStatsQuery(profileSlug, Boolean(data));
  // Highlight post stats + thumbnails (EPD-11945) stream in the same way.
  const { data: highlightPostStats } = usePublicHighlightStatsQuery(
    profileSlug,
    Boolean(data),
  );

  // Record one view per mount, once the portfolio is confirmed live.
  const { mutate: trackView } = useTrackPortfolioView();
  const trackedRef = useRef(false);
  useEffect(() => {
    if (data && !trackedRef.current) {
      trackedRef.current = true;
      trackView({ slug: profileSlug });
    }
  }, [data, profileSlug, trackView]);

  // The payload carries no contact email (EPD-12502); Contact fetches it.
  const loadContactEmail = useCallback(
    () => revealPortfolioContactEmail(profileSlug),
    [profileSlug],
  );

  // Set the browser-tab title for a live portfolio. Crawlers don't run JS: on
  // the marketing-domain host the web worker puts canonical + og:url in the
  // shell (EPD-10104).
  // Restore the prior title on unmount so navigating away doesn't leave it.
  const profileName = data?.profile.name;
  useEffect(() => {
    if (!profileName) return;
    const previous = document.title;
    document.title = `${profileName} · Portfolio`;
    return () => {
      document.title = previous;
    };
  }, [profileName]);

  // Draft or non-existent portfolios both return NOT_FOUND, so this copy must
  // not imply the slug exists.
  const notFoundContent = (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="text-2xl font-bold">Portfolio Not Found</h1>
      <p className="text-muted-foreground mt-2">
        This portfolio doesn’t exist or isn’t published.
      </p>
    </div>
  );

  // Every state but the portfolio itself (whose view brings its own <main>)
  // is wrapped here, so each state has exactly one main landmark (EPD-12471).
  let content: ReactNode;
  let viewHasMain = false;

  if (isPending) {
    content = <PublicPortfolioSkeleton />;
  } else if (isNotFound) {
    // NOT_FOUND wins over any stale `data` a focus/reconnect refetch may have
    // retained — a portfolio that was unpublished must never render from cache.
    content = notFoundContent;
  } else if (error) {
    content = (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="text-muted-foreground mt-2">
          We couldn’t load this portfolio. Please try again.
        </p>
        <Button
          className="mt-6"
          onClick={() => refetch()}
          disabled={isRefetching}
        >
          Try again
        </Button>
      </div>
    );
  } else if (!data) {
    content = notFoundContent;
  } else {
    const theme = parsePortfolioThemeJson(data.portfolioTheme);
    viewHasMain = true;
    content = (
      <PortfolioPublicView
        data={data}
        theme={theme}
        socialStats={socialStats}
        socialStatsPending={socialStatsPending}
        highlightPostStats={highlightPostStats}
        loadContactEmail={data.hasContactEmail ? loadContactEmail : undefined}
      />
    );
  }

  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      {viewHasMain ? content : <main>{content}</main>}
    </ThemeProvider>
  );
}
